import React, { useEffect } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GraduationCapIcon, BookOpenIcon, HeartIcon, UsersIcon, ShieldCheckIcon, ClockIcon, DollarSignIcon, AwardIcon, CheckCircleIcon, ArrowRightIcon, StarIcon, QuoteIcon, CheckIcon } from 'lucide-react';
import { getFindDegreesTemplate } from '../data/findDegreesTemplates';
import { getProgramTemplate, programsIndexSlug } from '../data/programTemplates';
import { ProgramTemplatePage } from './ProgramTemplatePage';
import { ProgramDomainsPage } from './ProgramDomainsPage';
import { usePageMeta } from '../hooks/usePageMeta';
import { buildFindDegreesClickout } from '../services/mediaMatchers/clickout';
import { getDegreeProgramCombo } from '../utils/degreeSlugs';
import { DegreeProgramLandingPage } from './DegreeProgramLandingPage';
const benefitIconMap: Record<string, React.ElementType> = {
  GraduationCapIcon,
  BookOpenIcon,
  HeartIcon,
  UsersIcon,
  ShieldCheckIcon,
  ClockIcon,
  DollarSignIcon,
  AwardIcon
};
/**
 * Resolves a slug to the right template: the program index, a domain page, an
 * individual program page, or the original lead-gen landing template. Explicit
 * routes are declared in App.tsx (so each URL shows up on its own), which means
 * the slug is read from the path when there is no `:template` param to read.
 *
 * Program content lives under `/program-information/`; anything reaching the
 * legacy `/find-degrees/` path with a program slug is redirected there so old
 * links keep working.
 */
export function FindDegreesPage() {
  const {
    template
  } = useParams<{
    template: string;
  }>();
  const location = useLocation();
  const pathSlug = location.pathname.split('/').filter(Boolean).pop() || '';
  const slug = (template || pathSlug).toLowerCase();
  const isLegacyPath = location.pathname.startsWith('/find-degrees/');
  const program = getProgramTemplate(slug);
  const isProgramContent = slug === programsIndexSlug || !!program;
  if (isLegacyPath && isProgramContent) {
    return <Navigate to={`/program-information/${slug}${location.search}`} replace data-id="element-1649" data-mp="2mlc7c6w5jlcj" />;
  }
  if (slug === programsIndexSlug) {
    return <ProgramDomainsPage data-id="element-1650" data-mp="28mulyftpykxs" />;
  }
  if (program) {
    return <ProgramTemplatePage key={program.slug} program={program} data-id="element-1651" data-mp="2clv37twjtfio" />;
  }
  // Generated degree-level × program landing pages, e.g.
  // /find-degrees/bachelors-business-administration
  const combo = getDegreeProgramCombo(slug);
  if (combo) {
    // Code-form URLs (e.g. /find-degrees/3-100) redirect to the readable
    // canonical slug so there is one indexable URL per combination. Query
    // parameters carry through untouched.
    if (slug !== combo.slug) {
      return <Navigate to={`/find-degrees/${combo.slug}${location.search}`} replace data-id="element-1652" data-mp="3jbbqswduc49w" />;
    }
    return <DegreeProgramLandingPage key={combo.slug} combo={combo} data-id="element-1653" data-mp="3o21ywhdnil8a" />;
  }
  return <FindDegreesLandingTemplate data-id="element-1654" data-mp="1cmjirxjhzo1d" />;
}
function FindDegreesLandingTemplate() {
  const {
    template
  } = useParams<{
    template: string;
  }>();
  const location = useLocation();
  const data = template ? getFindDegreesTemplate(template) : undefined;
  usePageMeta(data?.metaTitle || 'Find Your Degree | OnlineCollegeGuide', data?.metaDescription || 'Find accredited online degree programs that fit your goals.', {
    ogType: 'website'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [template]);
  if (!data) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4" data-id="element-1655" data-mp="oqf60kikx3lr">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-1656" data-mp="13npoidtn39qz">Page Not Found</h1>
        <p className="text-gray-600 mb-8" data-id="element-1657" data-mp="ym0bwtqrqo0s">
          We couldn't find the page you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-1658" data-mp="2ezyufwtnspnq">
          Return Home
        </Link>
      </div>;
  }
  const accent = data.accentColor;
  const accentDark = data.accentColorDark;
  // The CTAs click out to the Media Matchers search endpoint, forwarding every
  // query parameter on the current URL.
  const ctaHref = buildFindDegreesClickout(location.search);
  return <main className="min-h-screen bg-white" data-id="element-1659" data-mp="xldzvkfuntcz">
      {/* Minimal Top Bar */}
      <div className="bg-white border-b border-gray-100" data-id="element-1660" data-mp="xii4a2rh5grp">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between" data-id="element-1661" data-mp="12j63hbpux3x0">
          <Link to="/" className="flex items-center" data-id="element-1662" data-mp="17hhbri0i76d4">
            <img src="https://cdn.magicpatterns.com/uploads/hM6pwxGTxH7R3Ug6xJ6sm8/Horizontal_OCG_logo.png" alt="Online College Guide - Your Path Forward" className="h-10 w-auto object-contain" data-id="element-1663" data-mp="1ca0mm77t3ne8" />
          </Link>
          <div className="hidden sm:flex items-center text-xs font-semibold text-gray-500 tracking-wider uppercase" data-id="element-1664" data-mp="1l8cfkfw5ax9u">
            <ShieldCheckIcon className="w-4 h-4 mr-1.5 text-green-600" data-id="element-1665" data-mp="20dgumi80c1iy" />
            Trusted by 2M+ Students
          </div>
        </div>
      </div>

      {/* Hero - light theme matching reference layout */}
      <section className="bg-gray-50 relative overflow-hidden" data-id="element-1666" data-mp="1qf0zm45v0846">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20" data-id="element-1667" data-mp="1qhdgedzvscfn">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-id="element-1668" data-mp="1mfa27hltutqo">
            {/* Left: Copy */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-1669" data-mp="2j36mawgh476n">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-widest text-white mb-6" style={{
              backgroundColor: '#14479E'
            }} data-id="element-1670" data-mp="25cbbhemezg0f">
                <span className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: '#8FBEF5'
              }} data-id="element-1671" data-mp="v7i9voiwsdvd" />
                {data.heroEyebrowBadge || data.eyebrow}
              </span>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-gray-900 mb-6 tracking-tight" data-id="element-1672" data-mp="qeyvipcedepi">
                {data.heroHeadlinePrefix && <>{data.heroHeadlinePrefix} </>}
                {data.heroHeadlineHighlight && <span style={{
                color: '#14479E'
              }} data-id="element-1673" data-mp="hcpmk3ws1te8">
                    {data.heroHeadlineHighlight}
                  </span>}
                {data.heroHeadlineSuffix && <>
                    <br data-id="element-1674" data-mp="1wtgbvkz8vkl8" />
                    {data.heroHeadlineSuffix}
                  </>}
                {!data.heroHeadlinePrefix && !data.heroHeadlineHighlight && !data.heroHeadlineSuffix && data.headline}
              </h1>

              <p className="text-base sm:text-lg text-gray-600 max-w-xl leading-relaxed" data-id="element-1675" data-mp="3hbiixnwfy9cs">
                {data.subheadline}
              </p>
            </motion.div>

            {/* Right: Step indicator + Card */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} data-id="element-1676" data-mp="8xs1eil8q1cl">
              {/* Step Indicator */}
              <div className="flex items-center justify-between mb-5 px-2 sm:px-4" data-id="element-1677" data-mp="dxo8iqp8i8wd">
                <div className="flex items-center gap-2.5" data-id="element-1678" data-mp="2knum8uvjnjm9">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#1668E0'
                }} data-id="element-1679" data-mp="2forev3s3d1b4">
                    <CheckIcon className="w-4 h-4 text-white" strokeWidth={3} data-id="element-1680" data-mp="1ysub2pf1l5at" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900" data-id="element-1681" data-mp="23lds5wcxuikd">
                    {data.heroStep1Label || 'Profile Complete'}
                  </span>
                </div>
                <div className="flex-1 h-px bg-gray-300 mx-3 sm:mx-4" data-id="element-1682" data-mp="1ax9iz0v5rncx" />
                <div className="flex items-center gap-2.5" data-id="element-1683" data-mp="1furik0nacq89">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#14479E'
                }} data-id="element-1684" data-mp="1fxdq40a6f5qh">
                    <span className="text-xs font-bold text-white" data-id="element-1685" data-mp="2w9t244h7qaou">2</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-500" data-id="element-1686" data-mp="318dujhq83q2q">
                    {data.heroStep2Label || 'Confirm Email'}
                  </span>
                </div>
              </div>

              {/* Card */}
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 sm:p-10 text-center" data-id="element-1687" data-mp="kn8r9gkosnxc">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2" data-id="element-1688" data-mp="fu66nxxw0ota">
                  {data.heroCardHeading || data.headline}
                </h2>
                {data.heroCardSubtext && <p className="text-sm font-semibold mb-6" style={{
                color: '#14479E'
              }} data-id="element-1689" data-mp="5yuqrcaqtbjy">
                    {data.heroCardSubtext}
                  </p>}
                <a href={ctaHref} className="group inline-flex items-center justify-center text-white font-bold py-3.5 px-8 rounded-lg text-base transition-all duration-200 shadow-md hover:shadow-lg" style={{
                backgroundColor: '#FF9F04'
              }} onMouseEnter={e => {
                ;
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#E08C00';
              }} onMouseLeave={e => {
                ;
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#FF9F04';
              }} data-id="element-1690" data-mp="3o3y1pdne4hsa">
                  {data.ctaLabel}
                  <ArrowRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" data-id="element-1691" data-mp="2eboabnn0di6f" />
                </a>
                {data.heroCardHelper && <p className="mt-6 text-xs sm:text-sm text-gray-500 leading-relaxed max-w-xs mx-auto" data-id="element-1692" data-mp="2n6vvkoyobe6m">
                    {data.heroCardHelper}
                  </p>}
              </div>

              {/* Trust strip below card */}
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-gray-500" data-id="element-1693" data-mp="1qo6ffj8g63z8">
                {data.trustItems.map(item => <div key={item} className="flex items-center" data-id="element-1694" data-mp="1giy9tvo1xjd2">
                    <CheckCircleIcon className="w-3.5 h-3.5 text-green-600 mr-1.5 flex-shrink-0" data-id="element-1695" data-mp="1lc17aevy9cyj" />
                    {item}
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 lg:py-20 bg-white" data-id="element-1696" data-mp="1za7p6lki72ck">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1697" data-mp="1zbiu4opoqg59">
          <div className="text-center max-w-2xl mx-auto mb-12" data-id="element-1698" data-mp="248sq6n7kxq22">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{
            color: accent
          }} data-id="element-1699" data-mp="1pctj9fw7hvyi">
              {data.benefitsHeading}
            </h2>
            <div className="w-16 h-1 mx-auto rounded-full" style={{
            backgroundColor: '#1668E0'
          }} data-id="element-1700" data-mp="dwwfwmnmf9hr" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1701" data-mp="2liwlfjpf6mc1">
            {data.benefits.map((benefit, i) => {
            const Icon = benefitIconMap[benefit.icon] || BookOpenIcon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow" data-id="element-1702" data-mp="2bsbgzfn2enl7" data-mpi={i}>
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{
                backgroundColor: `${accent}15`
              }} data-id="element-1703" data-mp="3n80201grdcjd">
                    <Icon className="w-6 h-6" style={{
                  color: accent
                }} data-id="element-1704" data-mp="3us1673b1f4xn" />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{
                color: accent
              }} data-id="element-1705" data-mp="8c6rjgrtt04t">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1706" data-mp="3btyg0l6r71y9">
                    {benefit.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Featured Schools */}
      <section className="py-14 bg-gray-50 border-y border-gray-200" data-id="element-1707" data-mp="w2k9ufklzkm7">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1708" data-mp="w4dk04ufkmyy">
          <h2 className="text-center text-sm font-bold uppercase tracking-widest mb-8 text-gray-500" data-id="element-1709" data-mp="10zkfwqhgkndb">
            {data.schoolsHeading}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-1710" data-mp="ul56rijddr0h">
            {data.schoolNames.map(name => <div key={name} className="bg-white border border-gray-200 rounded-lg p-4 text-center flex items-center justify-center min-h-[80px]" data-id="element-1711" data-mp="18loc2hwgaiyy">
                <span className="text-sm font-semibold leading-tight" style={{
              color: accent
            }} data-id="element-1712" data-mp="1hk9qc0uw3btx">
                  {name}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      {data.testimonial && <section className="py-16 lg:py-20 bg-white" data-id="element-1713" data-mp="21927gbki36gy">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1714" data-mp="219k1r2nlram8">
            <QuoteIcon className="w-12 h-12 mx-auto mb-6 opacity-30" style={{
          color: accent
        }} data-id="element-1715" data-mp="1wbij2iu39dba" />
            <div className="flex items-center justify-center gap-1 mb-4" data-id="element-1716" data-mp="2jy4biyhrjbij">
              {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="w-5 h-5 fill-current" style={{
            color: '#5FA0EE'
          }} data-id="element-1717" data-mp="25wrz2x1t9s1e" />)}
            </div>
            <p className="text-xl sm:text-2xl font-medium leading-relaxed mb-6 italic" style={{
          color: accent
        }} data-id="element-1718" data-mp="1w5xavn508idb">
              "{data.testimonial.quote}"
            </p>
            <p className="font-bold text-gray-900" data-id="element-1719" data-mp="1s5n5abl4jtyz">{data.testimonial.name}</p>
            <p className="text-sm text-gray-500" data-id="element-1720" data-mp="1d9flvdhjiwkr">{data.testimonial.detail}</p>
          </div>
        </section>}

      {/* Bottom CTA */}
      <section className="py-16 lg:py-20" style={{
      backgroundColor: accentDark
    }} data-id="element-1721" data-mp="1i6xmmglem5ho">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1722" data-mp="z5qdtjoo5pni">
          <h2 className="text-2xl sm:text-4xl font-extrabold text-white mb-4 leading-tight" data-id="element-1723" data-mp="ubcej66vyydk">
            {data.bottomCtaHeading}
          </h2>
          <p className="text-base sm:text-lg text-gray-300 mb-8 leading-relaxed" data-id="element-1724" data-mp="keraoo6lrtbb">
            {data.bottomCtaBody}
          </p>
          <a href={ctaHref} className="group inline-flex items-center justify-center text-white font-bold py-4 px-10 rounded-lg text-base tracking-wide transition-all duration-200 shadow-lg hover:shadow-xl" style={{
          backgroundColor: '#FF9F04'
        }} onMouseEnter={e => {
          ;
          (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#E08C00';
        }} onMouseLeave={e => {
          ;
          (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#FF9F04';
        }} data-id="element-1725" data-mp="1i2rn3gp9abx9">
            {data.bottomCtaLabel}
            <ArrowRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" data-id="element-1726" data-mp="28ci8g2udkrkz" />
          </a>
          <p className="mt-5 text-xs text-gray-400 uppercase tracking-widest" data-id="element-1727" data-mp="2dbv5thivhh1t">
            Complementary • No Obligation • Takes 2 Minutes
          </p>
        </div>
      </section>

      {/* Footer mini */}
      <footer className="bg-gray-900 text-gray-400 text-xs py-6" data-id="element-1728" data-mp="2n1xythpzuv05">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-2" data-id="element-1729" data-mp="2n3r4atovf96k">
          <p data-id="element-1730" data-mp="2r52zdeqikmm5">
            &copy; {new Date().getFullYear()} OnlineCollegeGuide. All rights
            reserved.
          </p>
          <p className="max-w-3xl mx-auto leading-relaxed" data-id="element-1731" data-mp="310xlijty4zto">
            Advertiser Disclosure: OnlineCollegeGuide.com is an
            advertising-supported site. Featured schools compensate us, which
            does not influence our editorial content.
          </p>
          <div className="flex justify-center gap-4 pt-2" data-id="element-1732" data-mp="16p92o6klgd40">
            <Link to="/privacy-policy" className="hover:text-white transition-colors" data-id="element-1733" data-mp="38jncywcsya4p">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className="hover:text-white transition-colors" data-id="element-1734" data-mp="3ia8hf0f5q8vj">
              Terms of Service
            </Link>
          </div>
        </div>
      </footer>
    </main>;
}